import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

interface Template {
  id: string;
  name: string;
  description: string;
  category: 'business' | 'creative' | 'ecommerce' | 'newsletter';
  isActive: boolean;
  previewImage: string;
  createdAt: Date;
}

const AdminTemplateManager: React.FC = () => {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);

  // Form state for adding/editing templates
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'business' as Template['category'],
    previewImage: '',
    isActive: true
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      // In a real app, this would fetch from your backend
      // For now, we'll use mock data
      const mockTemplates: Template[] = [
        {
          id: '1',
          name: 'Cover',
          description: 'Dark theme with mountain silhouette',
          category: 'business',
          isActive: true,
          previewImage: '/api/placeholder/300/200',
          createdAt: new Date('2024-01-15')
        },
        {
          id: '2',
          name: 'Hanner',
          description: 'Light theme with kitchen image',
          category: 'creative',
          isActive: true,
          previewImage: '/api/placeholder/300/200',
          createdAt: new Date('2024-01-16')
        },
        {
          id: '3',
          name: 'Soul',
          description: 'Professional office theme',
          category: 'business',
          isActive: true,
          previewImage: '/api/placeholder/300/200',
          createdAt: new Date('2024-01-17')
        },
        {
          id: '4',
          name: 'Syml',
          description: 'Simple minimalist design',
          category: 'newsletter',
          isActive: false,
          previewImage: '/api/placeholder/300/200',
          createdAt: new Date('2024-01-18')
        },
        {
          id: '5',
          name: 'Tribe',
          description: 'Community-focused design',
          category: 'creative',
          isActive: true,
          previewImage: '/api/placeholder/300/200',
          createdAt: new Date('2024-01-19')
        }
      ];
      setTemplates(mockTemplates);
    } catch (error) {
      showError('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTemplate = async () => {
    try {
      const newTemplate: Template = {
        id: Date.now().toString(),
        name: formData.name,
        description: formData.description,
        category: formData.category,
        isActive: formData.isActive,
        previewImage: formData.previewImage,
        createdAt: new Date()
      };

      setTemplates([...templates, newTemplate]);
      setShowAddModal(false);
      resetForm();
      showSuccess('Template added successfully');
    } catch (error) {
      showError('Failed to add template');
    }
  };

  const handleEditTemplate = async (template: Template) => {
    try {
      setTemplates(templates.map(t => 
        t.id === template.id ? template : t
      ));
      setEditingTemplate(null);
      showSuccess('Template updated successfully');
    } catch (error) {
      showError('Failed to update template');
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        setTemplates(templates.filter(t => t.id !== templateId));
        showSuccess('Template deleted successfully');
      } catch (error) {
        showError('Failed to delete template');
      }
    }
  };

  const handleToggleActive = async (templateId: string) => {
    try {
      setTemplates(templates.map(t => 
        t.id === templateId ? { ...t, isActive: !t.isActive } : t
      ));
      showSuccess('Template status updated');
    } catch (error) {
      showError('Failed to update template status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'business',
      previewImage: '',
      isActive: true
    });
  };

  const openEditModal = (template: Template) => {
    setFormData({
      name: template.name,
      description: template.description,
      category: template.category,
      previewImage: template.previewImage,
      isActive: template.isActive
    });
    setEditingTemplate(template);
    setShowAddModal(true);
  };

  if (!currentUser) {
    return <div>Please log in to access this page.</div>;
  }

  return (
    <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Template Manager</h1>
              <p className="text-gray-600 mt-2">Manage landing page templates</p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-black hover:bg-gray-800 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
            >
              Add Template
            </button>
          </div>

          {/* Templates Grid */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading templates...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
                <div key={template.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  {/* Preview Image */}
                  <div className="h-48 bg-gray-200 flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <svg className="w-16 h-16 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm">Preview</p>
                    </div>
                  </div>

                  {/* Template Info */}
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        template.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {template.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-2">{template.description}</p>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                      <span className="capitalize">{template.category}</span>
                      <span>{template.createdAt.toLocaleDateString()}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openEditModal(template)}
                        className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors duration-200"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleActive(template.id)}
                        className={`flex-1 px-3 py-2 text-sm rounded transition-colors duration-200 ${
                          template.isActive
                            ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {template.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors duration-200"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add/Edit Modal */}
          {showAddModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  {editingTemplate ? 'Edit Template' : 'Add New Template'}
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Template Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Enter template name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Enter template description"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value as Template['category'] })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="business">Business</option>
                      <option value="creative">Creative</option>
                      <option value="ecommerce">E-commerce</option>
                      <option value="newsletter">Newsletter</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Preview Image URL
                    </label>
                    <input
                      type="url"
                      value={formData.previewImage}
                      onChange={(e) => setFormData({ ...formData, previewImage: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="mr-2"
                    />
                    <label htmlFor="isActive" className="text-sm text-gray-700">
                      Active (visible to users)
                    </label>
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingTemplate(null);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={editingTemplate ? () => handleEditTemplate({ ...editingTemplate, ...formData }) : handleAddTemplate}
                    className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors duration-200"
                  >
                    {editingTemplate ? 'Update' : 'Add'} Template
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
  );
};

export default AdminTemplateManager;
